import type { EnvironmentId, EnvironmentApi, OpenArtifactInput } from "@t3tools/contracts";

import type { WsRpcClient } from "@t3tools/client-runtime";
import { serverRunsOnThisMachine, downloadArtifact } from "./environments/primary/artifactDownload";
import { readEnvironmentConnection } from "./environments/runtime";

type ClientEnvironmentApi = Omit<EnvironmentApi, "shell"> & {
  shell: {
    openArtifact: (input: OpenArtifactInput, href?: string) => Promise<void>;
  };
};

const environmentApiOverridesForTests = new Map<EnvironmentId, ClientEnvironmentApi>();

export function createEnvironmentApi(rpcClient: WsRpcClient): ClientEnvironmentApi {
  return {
    projects: {
      searchEntries: rpcClient.projects.searchEntries,
      documentsStatus: rpcClient.projects.documentsStatus,
      deleteDocument: rpcClient.projects.deleteDocument,
      outputStatus: rpcClient.projects.outputStatus,
      copyOutputToMaterials: rpcClient.projects.copyOutputToMaterials,
      deleteOutputFile: rpcClient.projects.deleteOutputFile,
    },
    shell: {
      openArtifact: (input, href) =>
        serverRunsOnThisMachine()
          ? rpcClient.shell.openArtifact(input)
          : downloadArtifact(input, href),
    },
    orchestration: {
      dispatchCommand: rpcClient.orchestration.dispatchCommand,
      getArchivedShellSnapshot: rpcClient.orchestration.getArchivedShellSnapshot,
      subscribeShell: (callback, options) =>
        rpcClient.orchestration.subscribeShell(callback, options),
      subscribeThread: (input, callback, options) =>
        rpcClient.orchestration.subscribeThread(input, callback, options),
      exportThreadDebugLog: rpcClient.orchestration.exportThreadDebugLog,
    },
  };
}

export function readEnvironmentApi(environmentId: EnvironmentId): ClientEnvironmentApi | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  if (!environmentId) {
    return undefined;
  }

  const overriddenApi = environmentApiOverridesForTests.get(environmentId);
  if (overriddenApi) {
    return overriddenApi;
  }

  const connection = readEnvironmentConnection(environmentId);
  return connection ? createEnvironmentApi(connection.client) : undefined;
}

export function ensureEnvironmentApi(environmentId: EnvironmentId): ClientEnvironmentApi {
  const api = readEnvironmentApi(environmentId);
  if (!api) {
    throw new Error(`Environment API not found for environment ${environmentId}`);
  }
  return api;
}

export function __setEnvironmentApiOverrideForTests(
  environmentId: EnvironmentId,
  api: EnvironmentApi,
): void {
  environmentApiOverridesForTests.set(environmentId, api);
}

export function __resetEnvironmentApiOverridesForTests(): void {
  environmentApiOverridesForTests.clear();
}
